import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Ban, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { UnsellableMovementRow } from '../types'
import { nextUnsellableCode } from '../types'
import { getUnsellableMovement, listUnsellableMovements, upsertUnsellableMovement } from '../storage'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { inventoryMovementService } from '@/services/inventoryMovementService'
import { unsellableInventoryMovementService } from '@/services/unsellableInventoryMovementService'
import type { ItemEntity, Warehouse } from '@/api/types'

type ApiBatchRow = { id: number; code: string; reference: string; availableQuantity: number }

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function parseNonNeg(s: string): number {
  const n = Number(s)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.floor(n)
}

function clampAvailableQty(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.floor(n))
}

/** Radix Select needs a non-empty `value` from first paint — avoids uncontrolled → controlled input warnings. */
const SELECT_EMPTY = '__unsellable_empty__'

function isEmptySelection(v: string) {
  return v === '' || v === SELECT_EMPTY
}

/** Route param e.g. `api-12` → server id `12`. */
function parseUnsellableRouteApiId(routeId: string): number | null {
  const m = /^api-(\d+)$/.exec(routeId)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

export const UnsellableInventoryEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const isNew = !id || id === 'new'
  const viewOnly = !isNew && location.pathname.endsWith('/view')

  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [warehousesLoading, setWarehousesLoading] = useState(false)
  const [items, setItems] = useState<ItemEntity[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [batches, setBatches] = useState<ApiBatchRow[]>([])
  const [batchesLoading, setBatchesLoading] = useState(false)

  const [warehouseId, setWarehouseId] = useState(SELECT_EMPTY)
  const [itemId, setItemId] = useState(SELECT_EMPTY)
  const [batchId, setBatchId] = useState(SELECT_EMPTY)
  const [movementDate, setMovementDate] = useState(todayIso())
  const [currentQuantity, setCurrentQuantity] = useState(0)
  const [currentQtyLoading, setCurrentQtyLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [moveDamaged, setMoveDamaged] = useState('0')
  const [moveExpired, setMoveExpired] = useState('0')
  /** When GetById has itemId/batchId but transfer APIs omit that row, Radix Select still needs a matching option. */
  const [lookupSeed, setLookupSeed] = useState<{ itemLabel: string; batchLabel: string } | null>(null)

  const itemOptions = useMemo(() => {
    return items.map((it) => {
      const name =
        language === 'ar' ? it.itemNameAr || it.itemNameEn || it.itemCode : it.itemNameEn || it.itemNameAr || it.itemCode
      const label = `${it.itemCode || ''}${it.itemCode ? ' — ' : ''}${name || ''}`.trim()
      return { value: String(it.itemId), label }
    })
  }, [items, language])

  const itemOptionsDisplay = useMemo(() => {
    const base = [...itemOptions]
    if (!isEmptySelection(itemId) && !base.some((o) => o.value === itemId)) {
      const label = lookupSeed?.itemLabel?.trim() || itemId
      base.push({ value: itemId, label })
    }
    return base
  }, [itemOptions, itemId, lookupSeed])

  const batchesForDisplay = useMemo((): ApiBatchRow[] => {
    const list = [...batches]
    if (isEmptySelection(batchId)) return list
    if (list.some((b) => String(b.id) === batchId)) return list
    const n = Number(batchId)
    if (!Number.isFinite(n) || n <= 0) return list
    const code = lookupSeed?.batchLabel?.trim() || batchId
    list.push({ id: n, code, reference: '', availableQuantity: 0 })
    return list
  }, [batches, batchId, lookupSeed])

  const damagedN = parseNonNeg(moveDamaged)
  const expiredN = parseNonNeg(moveExpired)
  const newQuantity = Math.max(0, currentQuantity - damagedN - expiredN)

  const prevBranchIdRef = useRef(0)
  /** Row last loaded from GetById / storage / list state — used on save for `apiId` / `code`. */
  const loadedRowRef = useRef<UnsellableMovementRow | null>(null)

  useEffect(() => {
    if (!selectedBranchId) {
      setWarehouses([])
      prevBranchIdRef.current = 0
      return
    }
    if (prevBranchIdRef.current !== 0 && prevBranchIdRef.current !== selectedBranchId) {
      setWarehouseId(SELECT_EMPTY)
      setItemId(SELECT_EMPTY)
      setBatchId(SELECT_EMPTY)
      setLookupSeed(null)
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setWarehousesLoading(true)
    void inventoryMovementService
      .getWarehousesByBranchId(selectedBranchId)
      .then((list) => {
        if (!cancelled) setWarehouses(list)
      })
      .catch((e: any) => {
        if (!cancelled) {
          setWarehouses([])
          const msg = e?.response?.data?.message
          toast.error(msg || t('unsellableInventory.warehousesLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setWarehousesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedBranchId, t])

  useEffect(() => {
    if (isEmptySelection(warehouseId)) {
      setItems([])
      setItemsLoading(false)
      return
    }
    const whNum = Number(warehouseId)
    if (!whNum) {
      setItems([])
      return
    }
    let cancelled = false
    setItemsLoading(true)
    void inventoryMovementService
      .getItemsByWarehouse(whNum)
      .then((list) => {
        if (!cancelled) setItems(list)
      })
      .catch((e: any) => {
        if (!cancelled) {
          setItems([])
          const msg = e?.response?.data?.message
          toast.error(msg || t('unsellableInventory.itemsLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [warehouseId, t])

  useEffect(() => {
    if (isEmptySelection(warehouseId) || isEmptySelection(itemId)) {
      setBatches([])
      setBatchesLoading(false)
      return
    }
    const whNum = Number(warehouseId)
    const itNum = Number(itemId)
    if (!whNum || !itNum) {
      setBatches([])
      return
    }
    let cancelled = false
    setBatchesLoading(true)
    void inventoryMovementService
      .getBatchesByWarehouseAndItem(whNum, itNum)
      .then((list) => {
        if (!cancelled) setBatches(list)
      })
      .catch((e: any) => {
        if (!cancelled) {
          setBatches([])
          const msg = e?.response?.data?.message
          toast.error(msg || t('unsellableInventory.batchesLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setBatchesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [warehouseId, itemId, t])

  useEffect(() => {
    if (isNew) {
      loadedRowRef.current = null
      return
    }
    let cancelled = false
    const routeId = id!
    const apiNumericId = parseUnsellableRouteApiId(routeId)

    const applyRow = (row: UnsellableMovementRow) => {
      if (cancelled) return
      loadedRowRef.current = row
      setLookupSeed({
        itemLabel: row.itemLabel?.trim() ?? '',
        batchLabel: row.batchLabel?.trim() ?? '',
      })
      setWarehouseId(isEmptySelection(row.warehouseId) ? SELECT_EMPTY : row.warehouseId)
      setItemId(isEmptySelection(row.itemId) ? SELECT_EMPTY : row.itemId)
      setBatchId(isEmptySelection(row.batchId) ? SELECT_EMPTY : row.batchId)
      setMovementDate(row.movementDate?.slice(0, 10) || todayIso())
      setMoveDamaged(String(row.damaged))
      setMoveExpired(String(row.expired))
      setCurrentQuantity(row.previousQuantity)
    }

    void (async () => {
      if (apiNumericId != null) {
        try {
          const row = await unsellableInventoryMovementService.getById(apiNumericId)
          if (!cancelled && row) {
            applyRow(row)
            return
          }
        } catch (e: any) {
          if (!cancelled) {
            const msg = e?.response?.data?.message
            toast.error(msg || t('unsellableInventory.detailLoadFailed'))
          }
        }
      }

      const fromStorage = getUnsellableMovement(routeId)
      const fromList = (location.state as { movement?: UnsellableMovementRow } | null)?.movement
      const row = fromStorage ?? (fromList?.id === routeId ? fromList : undefined)
      if (cancelled) return
      if (!row) {
        toast.error(t('unsellableInventory.notFound'))
        navigate('/inventory/unsellable-movement')
        return
      }
      applyRow(row)
    })()

    return () => {
      cancelled = true
    }
  }, [id, isNew, location.state, navigate, t])

  useEffect(() => {
    if (!isNew) return
    if (isEmptySelection(warehouseId) || isEmptySelection(itemId) || isEmptySelection(batchId)) {
      setCurrentQuantity(0)
      setCurrentQtyLoading(false)
      return
    }
    const whNum = Number(warehouseId)
    const itNum = Number(itemId)
    const bNum = Number(batchId)
    if (!whNum || !itNum || !bNum) {
      setCurrentQuantity(0)
      setCurrentQtyLoading(false)
      return
    }
    let cancelled = false
    setCurrentQtyLoading(true)
    setCurrentQuantity(0)
    void inventoryMovementService
      .getItemDetails(whNum, itNum, bNum)
      .then((d) => {
        if (!cancelled) setCurrentQuantity(clampAvailableQty(d.availableQuantity))
      })
      .catch((e: any) => {
        if (!cancelled) {
          setCurrentQuantity(0)
          const msg = e?.response?.data?.message
          toast.error(msg || t('unsellableInventory.itemDetailsLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setCurrentQtyLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [warehouseId, itemId, batchId, isNew, t])

  useEffect(() => {
    if (batchesLoading || isEmptySelection(batchId)) return
    const inList = batches.some((b) => String(b.id) === batchId)
    if (inList) return
    // Loaded movement (GetById / list state): keep batch even if transfer API omits it — synthetic option shows label/id.
    if (lookupSeed !== null) return
    if (batches.length === 0) {
      setBatchId(SELECT_EMPTY)
      return
    }
    setBatchId(SELECT_EMPTY)
  }, [batches, batchesLoading, batchId, lookupSeed])

  useEffect(() => {
    if (isNew) setLookupSeed(null)
  }, [isNew])

  const warehouseName = useMemo(() => {
    const w = warehouses.find((x) => String(x.id) === warehouseId)
    if (!w) return ''
    const c = w.code?.trim()
    const n = w.name?.trim()
    if (c && n) return `${c} — ${n}`
    return n || c || String(w.id)
  }, [warehouses, warehouseId])
  const itemLabel = useMemo(
    () => itemOptionsDisplay.find((o) => o.value === itemId)?.label ?? '',
    [itemOptionsDisplay, itemId],
  )
  const batchLabel = useMemo(() => {
    const b = batchesForDisplay.find((x) => String(x.id) === batchId)
    if (!b) return ''
    const c = b.code?.trim()
    return c || String(b.id)
  }, [batchesForDisplay, batchId])

  const handleBack = () => navigate('/inventory/unsellable-movement')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (viewOnly) return
    if (isEmptySelection(warehouseId) || isEmptySelection(itemId) || isEmptySelection(batchId)) {
      toast.error(t('unsellableInventory.validationBatchItem'))
      return
    }
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.saveNoBranch'))
      return
    }
    if (damagedN + expiredN > currentQuantity) {
      toast.error(t('unsellableInventory.validationQty'))
      return
    }

    const all = listUnsellableMovements()
    const rowId = isNew ? `usm-${Date.now()}` : id!
    const fromList = (location.state as { movement?: UnsellableMovementRow } | null)?.movement
    const existing = !isNew
      ? getUnsellableMovement(rowId) ??
        (fromList?.id === rowId ? fromList : undefined) ??
        loadedRowRef.current ??
        undefined
      : undefined
    const code = isNew ? nextUnsellableCode(all) : existing?.code ?? nextUnsellableCode(all)

    const payload = {
      id: isNew ? 0 : existing?.apiId && existing.apiId > 0 ? existing.apiId : 0,
      code,
      warehouseId: Number(warehouseId),
      itemId: Number(itemId),
      batchId: Number(batchId),
      date: movementDate.slice(0, 10),
      prevAvailableQuantity: currentQuantity,
      movedToDamaged: damagedN,
      movedToExpired: expiredN,
      newAvailableQuantity: newQuantity,
      branchId: selectedBranchId,
    }

    console.log('[UnsellableInventoryEdit] Save payload (exact JSON):', JSON.stringify(payload, null, 2))

    setSaveLoading(true)
    try {
      const returnedId = await unsellableInventoryMovementService.save(payload)
      const row: UnsellableMovementRow = {
        id: rowId,
        apiId: returnedId ?? existing?.apiId,
        code,
        warehouseId,
        warehouseName,
        itemId,
        itemLabel,
        batchId,
        batchLabel,
        previousQuantity: currentQuantity,
        damaged: damagedN,
        expired: expiredN,
        newQuantity,
        movementDate,
      }
      upsertUnsellableMovement(row)
      await Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('unsellableInventory.saveSuccess'),
        confirmButtonText: t('common.ok'),
      })
      navigate('/inventory/unsellable-movement')
    } catch (err: any) {
      console.error('[UnsellableInventoryEdit] Save failed — see [InventoryMovement/Save] logs above for full API response.')
      console.error('[UnsellableInventoryEdit] err.response?.data:', err?.response?.data)
      const data = err?.response?.data as Record<string, unknown> | string | undefined
      let msg = ''
      if (typeof data === 'string') msg = data
      else if (data && typeof data === 'object') {
        msg = String(data.message ?? data.Message ?? data.msg ?? data.Msg ?? '')
      }
      if (!msg.trim() && err?.message) msg = String(err.message)
      toast.error(msg.trim() || t('unsellableInventory.saveFailed'))
    } finally {
      setSaveLoading(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Ban className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('unsellableInventory.formTitle')}</h1>
          <p className="text-muted-foreground text-sm">{t('unsellableInventory.formSubtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('unsellableInventory.sectionBatchItem')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>
                {t('unsellableInventory.warehouse')} <span className="text-destructive">*</span>
              </Label>
              {viewOnly ? (
                <Input readOnly value={warehouseName || '—'} className="bg-muted/20" />
              ) : (
                <Select
                  value={warehouseId}
                  onValueChange={(v) => {
                    setWarehouseId(v)
                    setItemId(SELECT_EMPTY)
                    setBatchId(SELECT_EMPTY)
                    setLookupSeed(null)
                  }}
                  disabled={!selectedBranchId || warehousesLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_EMPTY}>
                      {!selectedBranchId
                        ? t('unsellableInventory.selectBranchForWarehouses')
                        : warehousesLoading
                          ? t('common.loading', { defaultValue: 'Loading…' })
                          : t('unsellableInventory.selectWarehouse')}
                    </SelectItem>
                    {warehouses.map((w) => {
                      const c = w.code?.trim()
                      const n = w.name?.trim()
                      const label = c && n ? `${c} — ${n}` : n || c || String(w.id)
                      return (
                        <SelectItem key={w.id} value={String(w.id)}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                {t('unsellableInventory.item')} <span className="text-destructive">*</span>
              </Label>
              {viewOnly ? (
                <Input readOnly value={itemLabel || '—'} className="bg-muted/20" />
              ) : (
                <Select
                  value={itemId}
                  onValueChange={(v) => {
                    setItemId(v)
                    setBatchId(SELECT_EMPTY)
                    setLookupSeed(null)
                  }}
                  disabled={isEmptySelection(warehouseId) || itemsLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_EMPTY}>
                      {isEmptySelection(warehouseId)
                        ? t('unsellableInventory.selectWarehouseForItems')
                        : itemsLoading
                          ? t('common.loading', { defaultValue: 'Loading…' })
                          : t('unsellableInventory.selectItem')}
                    </SelectItem>
                    {itemOptionsDisplay.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                {t('unsellableInventory.batch')} <span className="text-destructive">*</span>
              </Label>
              {viewOnly ? (
                <Input readOnly value={batchLabel || '—'} className="bg-muted/20" />
              ) : (
                <Select
                  value={batchId}
                  onValueChange={setBatchId}
                  disabled={isEmptySelection(warehouseId) || isEmptySelection(itemId) || batchesLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_EMPTY}>
                      {isEmptySelection(warehouseId) || isEmptySelection(itemId)
                        ? t('unsellableInventory.selectWarehouseItemForBatches')
                        : batchesLoading
                          ? t('common.loading', { defaultValue: 'Loading…' })
                          : t('unsellableInventory.selectBatch')}
                    </SelectItem>
                    {batchesForDisplay.map((b) => {
                      const c = b.code?.trim()
                      const label = c || String(b.id)
                      return (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('unsellableInventory.sectionMovement')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="usm-date">{t('unsellableInventory.date')}</Label>
                <Input
                  id="usm-date"
                  type="date"
                  readOnly={viewOnly}
                  value={movementDate}
                  onChange={(e) => setMovementDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usm-curr">{t('unsellableInventory.currentQuantity')}</Label>
                <Input
                  id="usm-curr"
                  type="number"
                  min={0}
                  readOnly
                  aria-busy={currentQtyLoading}
                  title={currentQtyLoading ? t('unsellableInventory.currentQtyLoadingHint') : undefined}
                  className="bg-muted"
                  value={currentQtyLoading ? 0 : currentQuantity}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usm-dmg">{t('unsellableInventory.moveToDamaged')}</Label>
                <Input
                  id="usm-dmg"
                  type="number"
                  min={0}
                  readOnly={viewOnly}
                  value={moveDamaged}
                  onChange={(e) => setMoveDamaged(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="usm-exp">{t('unsellableInventory.moveToExpired')}</Label>
                <Input
                  id="usm-exp"
                  type="number"
                  min={0}
                  readOnly={viewOnly}
                  value={moveExpired}
                  onChange={(e) => setMoveExpired(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usm-new">{t('unsellableInventory.newQuantity')}</Label>
                <Input id="usm-new" type="number" readOnly className="bg-muted" value={newQuantity} />
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="w-full sm:w-auto sm:order-1"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.back')}
              </Button>
              {!viewOnly ? (
                <Button
                  type="submit"
                  disabled={currentQtyLoading || saveLoading}
                  className="w-full sm:w-auto sm:order-2"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {!isNew ? t('common.update') : t('unsellableInventory.save')}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
